/*
 * Ricordo Winter — site-wide Christmas snowfall.
 * One fixed canvas that never takes clicks. Three depths of snow (far powder, round flakes, six-armed
 * crystals close to the eye), a soft changing wind, depth parallax while the page scrolls, flakes that
 * swirl away from the pointer, crystals that catch the light, and a drift that settles on the footer.
 * Respects prefers-reduced-motion, pauses in background tabs, adapts to slow devices and remembers the
 * visitor's on/off choice.
 */
(function () {
  'use strict';
  if (window.__rcSnow) return;
  window.__rcSnow = true;

  var config = document.getElementById('rc-snow-config');
  if (!config) return;

  var STORAGE_KEY = 'ricordo-snow';
  var intensity = { light: 0.55, cozy: 1, blizzard: 1.8 }[config.getAttribute('data-intensity')] || 1;
  var driftEnabled = config.getAttribute('data-drift') === 'true';
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var finePointer = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : false;

  var saved = null;
  try { saved = window.localStorage.getItem(STORAGE_KEY); } catch (e) { /* storage unavailable */ }
  var on = saved === 'on' ? true : saved === 'off' ? false : !reduceMotion.matches;

  var canvas = document.createElement('canvas');
  canvas.className = 'rc-snow';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.setAttribute('role', 'presentation');
  document.body.appendChild(canvas);
  var ctx = canvas.getContext('2d');
  if (!ctx) return;

  var W = 0, H = 0, DPR = 1, isSmall = false;
  var flakes = [];
  var running = false, rafId = 0, lastT = 0, time = 0;
  var qualityScale = 1, perfSamples = 0, perfTotal = 0, perfChecks = 0;
  var lastScrollY = window.scrollY || 0, scrollPush = 0;
  var pointer = { x: -9999, y: -9999, active: false };
  var footerEl = null;
  var drift = null, driftBin = 6, driftMax = 16, driftVisible = false;

  /* ---------- Sprites (drawn once, with a soft cool shadow so snow also reads on white pages) ---------- */
  function makeCanvas(size) {
    var c = document.createElement('canvas');
    c.width = c.height = size;
    return c;
  }

  function makeDot() {
    var size = 32, c = makeCanvas(size), g = c.getContext('2d');
    var grad = g.createRadialGradient(16, 16, 0, 16, 16, 15);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.45, 'rgba(255,255,255,0.95)');
    grad.addColorStop(0.62, 'rgba(232,240,250,0.9)');
    grad.addColorStop(0.78, 'rgba(120,145,180,0.5)');
    grad.addColorStop(0.9, 'rgba(110,130,165,0.18)');
    grad.addColorStop(1, 'rgba(110,130,165,0)');
    g.fillStyle = grad;
    g.beginPath();
    g.arc(16, 16, 15, 0, Math.PI * 2);
    g.fill();
    return c;
  }

  function makeCrystal(variant) {
    var size = 64, c = makeCanvas(size), g = c.getContext('2d');
    var R = 24;
    g.translate(32, 32);
    g.lineCap = 'round';
    g.lineJoin = 'round';
    g.shadowColor = 'rgba(35,55,90,0.75)';
    g.shadowBlur = 3;
    g.strokeStyle = 'rgba(255,255,255,0.98)';
    g.lineWidth = 2.6;
    for (var i = 0; i < 6; i++) {
      g.save();
      g.rotate((Math.PI / 3) * i);
      g.beginPath();
      g.moveTo(0, 0);
      g.lineTo(0, -R);
      if (variant === 0) {
        g.moveTo(0, -R * 0.5); g.lineTo(-R * 0.26, -R * 0.72);
        g.moveTo(0, -R * 0.5); g.lineTo(R * 0.26, -R * 0.72);
        g.moveTo(0, -R * 0.78); g.lineTo(-R * 0.16, -R * 0.92);
        g.moveTo(0, -R * 0.78); g.lineTo(R * 0.16, -R * 0.92);
      } else if (variant === 1) {
        g.moveTo(0, -R * 0.35); g.lineTo(-R * 0.3, -R * 0.55);
        g.moveTo(0, -R * 0.35); g.lineTo(R * 0.3, -R * 0.55);
        g.moveTo(-R * 0.14, -R * 0.82); g.lineTo(R * 0.14, -R * 0.82);
      } else if (variant === 2) {
        g.moveTo(0, -R * 0.62); g.lineTo(-R * 0.34, -R * 0.84);
        g.moveTo(0, -R * 0.62); g.lineTo(R * 0.34, -R * 0.84);
      } else {
        g.moveTo(-R * 0.18, -R * 0.4); g.lineTo(0, -R * 0.55); g.lineTo(R * 0.18, -R * 0.4);
        g.moveTo(-R * 0.14, -R * 0.7); g.lineTo(0, -R * 0.82); g.lineTo(R * 0.14, -R * 0.7);
      }
      g.stroke();
      g.restore();
    }
    g.shadowBlur = 0;
    g.fillStyle = '#ffffff';
    g.beginPath();
    g.arc(0, 0, 3, 0, Math.PI * 2);
    g.fill();
    return c;
  }

  function makeGlint() {
    var size = 64, c = makeCanvas(size), g = c.getContext('2d');
    g.translate(32, 32);
    var grad = g.createRadialGradient(0, 0, 0, 0, 0, 14);
    grad.addColorStop(0, 'rgba(255,255,255,0.95)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.beginPath();
    g.arc(0, 0, 14, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = 'rgba(255,255,255,0.95)';
    for (var i = 0; i < 4; i++) {
      g.save();
      g.rotate((Math.PI / 2) * i);
      g.beginPath();
      g.moveTo(-1.6, 0);
      g.lineTo(0, -30);
      g.lineTo(1.6, 0);
      g.closePath();
      g.fill();
      g.restore();
    }
    return c;
  }

  var dotSprite = makeDot();
  var crystalSprites = [makeCrystal(0), makeCrystal(1), makeCrystal(2), makeCrystal(3)];
  var glintSprite = makeGlint();

  /* ---------- Flakes ---------- */
  function rand(a, b) { return a + Math.random() * (b - a); }

  function targetCount() {
    var area = W * H;
    var perFlake = isSmall ? 5200 : 7600;
    var cap = isSmall ? 110 : 260;
    return Math.max(24, Math.round(Math.min(cap * intensity, (area / perFlake) * intensity) * qualityScale));
  }

  function makeFlake(anywhere) {
    var z = Math.pow(Math.random(), 1.6); // most snow is far away, a few flakes come close
    var crystal = z > 0.72 && Math.random() < 0.75;
    var f = {
      z: z,
      crystal: crystal,
      sprite: crystal ? crystalSprites[(Math.random() * 4) | 0] : dotSprite,
      size: crystal ? rand(12, 22) * (isSmall ? 0.85 : 1) : 2 + z * rand(4, 8),
      vy: 18 + z * rand(45, 70),
      swayAmp: rand(6, 22) * (0.4 + z),
      swayFreq: rand(0.4, 1.3),
      phase: rand(0, Math.PI * 2),
      rot: rand(0, Math.PI * 2),
      vr: crystal ? rand(-0.9, 0.9) : 0,
      alpha: crystal ? rand(0.75, 0.95) : 0.35 + z * 0.6,
      vx: 0,
      glint: 0,
      x: rand(-20, W + 20),
      y: 0
    };
    f.y = anywhere ? rand(-20, H) : rand(-60, -f.size);
    return f;
  }

  function syncCount() {
    var n = targetCount();
    while (flakes.length < n) flakes.push(makeFlake(true));
    if (flakes.length > n) flakes.length = n;
  }

  /* ---------- Footer drift ---------- */
  function findFooter() {
    footerEl = document.querySelector('.shopify-section-group-footer-group') || document.querySelector('footer');
  }

  function resetDrift() {
    if (!driftEnabled) return;
    driftBin = isSmall ? 5 : 6;
    driftMax = isSmall ? 10 : 16;
    drift = new Float32Array(Math.ceil(W / driftBin) + 2);
  }

  function addToDrift(x, amount) {
    var i = Math.round(x / driftBin);
    for (var k = -3; k <= 3; k++) {
      var j = i + k;
      if (j < 0 || j >= drift.length) continue;
      var fall = 1 - Math.abs(k) / 4;
      drift[j] = Math.min(driftMax * (0.75 + 0.25 * Math.sin(j * 0.37)), drift[j] + amount * fall);
    }
  }

  function drawDrift(top) {
    var any = false;
    for (var i = 0; i < drift.length; i++) { if (drift[i] > 0.3) { any = true; break; } }
    if (!any) return;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, top + 2);
    ctx.lineTo(0, top - drift[0]);
    for (var j = 1; j < drift.length; j++) {
      var x0 = (j - 1) * driftBin, x1 = j * driftBin;
      var y0 = top - drift[j - 1], y1 = top - drift[j];
      ctx.quadraticCurveTo(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
    }
    ctx.lineTo(W, top - drift[drift.length - 1]);
    ctx.lineTo(W, top + 2);
    ctx.closePath();
    var grad = ctx.createLinearGradient(0, top - driftMax, 0, top + 2);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.55, '#f1f5fb');
    grad.addColorStop(1, '#d9e3f0');
    ctx.fillStyle = grad;
    ctx.shadowColor = 'rgba(35,55,90,0.4)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = -1;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(120,145,180,0.55)';
    ctx.stroke();
    ctx.restore();
    // A few sparkles twinkle in the drift.
    for (var s = 0; s < 3; s++) {
      var b = (((time * 0.7 + s * 13.1) * 37) | 0) % drift.length;
      var h = drift[b];
      if (h < 4) continue;
      var tw = Math.max(0, Math.sin(time * 3 + s * 2.1));
      if (tw < 0.2) continue;
      ctx.globalAlpha = tw * 0.9;
      ctx.drawImage(glintSprite, b * driftBin - 6, top - h * 0.6 - 6, 12, 12);
    }
    ctx.globalAlpha = 1;
  }

  /* ---------- Sizing ---------- */
  function resize() {
    var newW = canvas.clientWidth || window.innerWidth;
    var newH = canvas.clientHeight || window.innerHeight;
    var widthChanged = Math.abs(newW - W) > 1;
    W = newW;
    H = newH;
    isSmall = W < 750;
    DPR = Math.min(window.devicePixelRatio || 1, isSmall ? 1.5 : 2);
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    if (widthChanged) {
      // Only a real width change re-seeds the snow; a phone's toolbar sliding in and out does not.
      flakes = [];
      syncCount();
      resetDrift();
    }
  }

  /* ---------- Loop ---------- */
  function frame(now) {
    if (!running) return;
    rafId = window.requestAnimationFrame(frame);
    var dtMs = lastT ? now - lastT : 16;
    lastT = now;
    var dt = Math.min(dtMs, 50) / 1000;
    time += dt;

    // Adapt to slow devices: after a short warm-up, thin the snow if frames are slow.
    if (perfChecks < 3) {
      perfTotal += dtMs; perfSamples++;
      if (perfSamples === 90) {
        if (perfTotal / perfSamples > 26 && qualityScale > 0.4) { qualityScale *= 0.7; syncCount(); }
        perfTotal = 0; perfSamples = 0; perfChecks++;
      }
    }

    // Scroll parallax: near flakes move more than far ones, so the snow has depth.
    var sy = window.scrollY || 0;
    var dScroll = sy - lastScrollY;
    lastScrollY = sy;
    if (Math.abs(dScroll) > 200) dScroll = 0; // jumps (anchors, restores) do not fling the snow
    scrollPush = scrollPush * 0.9 + dScroll * 0.1;

    var wind = Math.sin(time * 0.13) * 14 + Math.sin(time * 0.051 + 1.3) * 22 + Math.sin(time * 0.7) * 4;

    var footerTop = -1;
    if (drift && footerEl) {
      var rect = footerEl.getBoundingClientRect();
      footerTop = rect.top;
      driftVisible = footerTop > 40 && footerTop < H + driftMax;
      if (!driftVisible) footerTop = -1;
    }

    ctx.clearRect(0, 0, W, H);

    for (var i = 0; i < flakes.length; i++) {
      var f = flakes[i];
      var prevY = f.y;

      if (pointer.active) {
        var dx = f.x - pointer.x, dy = f.y - pointer.y;
        var d2 = dx * dx + dy * dy;
        if (d2 < 10000 && d2 > 0.01) {
          var d = Math.sqrt(d2), push = (1 - d / 100) * 260 * (0.3 + f.z);
          f.vx += (dx / d) * push * dt;
          f.y += (dy / d) * push * dt * 0.4;
        }
      }
      f.vx *= 0.95;

      f.x += (wind * (0.35 + f.z) + Math.sin(time * f.swayFreq + f.phase) * f.swayAmp + f.vx) * dt;
      f.y += f.vy * dt - scrollPush * (0.15 + f.z * 0.55);
      f.rot += f.vr * dt;

      // Settle on the footer (only snow from the front layers lands; far powder falls behind it).
      if (footerTop > 0 && f.z > 0.3) {
        var bi = Math.round(f.x / driftBin);
        var surface = footerTop - (drift[bi] || 0);
        if (prevY < surface && f.y >= surface && f.x > 0 && f.x < W) {
          addToDrift(f.x, f.crystal ? 2.2 : 0.7 + f.z * 0.9);
          flakes[i] = makeFlake(false);
          continue;
        }
      }

      if (f.y > H + f.size) { flakes[i] = makeFlake(false); continue; }
      if (f.y < -f.size - 80) f.y = H + f.size - 1;
      if (f.x > W + 30) f.x = -30;
      else if (f.x < -30) f.x = W + 30;

      var s = f.size;
      ctx.globalAlpha = f.alpha;
      if (f.crystal) {
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rot);
        ctx.drawImage(f.sprite, -s / 2, -s / 2, s, s);
        ctx.restore();
        if (f.glint <= 0 && Math.random() < dt * 0.05) f.glint = 1;
        if (f.glint > 0) {
          var g = Math.sin(f.glint * Math.PI);
          ctx.globalAlpha = g;
          var gs = s * 1.3;
          ctx.drawImage(glintSprite, f.x - gs / 2, f.y - gs / 2, gs, gs);
          f.glint -= dt * 1.4;
        }
      } else {
        ctx.drawImage(f.sprite, f.x - s / 2, f.y - s / 2, s, s);
      }
    }
    ctx.globalAlpha = 1;

    if (drift) {
      var melt = 0.12 * dt;
      for (var m = 0; m < drift.length; m++) if (drift[m] > 0) drift[m] = Math.max(0, drift[m] - melt);
      if (footerTop > 0) drawDrift(footerTop);
    }
  }

  function start() {
    if (running || !on || document.hidden) return;
    running = true;
    lastT = 0;
    lastScrollY = window.scrollY || 0;
    canvas.classList.add('is-on');
    rafId = window.requestAnimationFrame(frame);
  }

  function stop(clear) {
    running = false;
    if (rafId) window.cancelAnimationFrame(rafId);
    rafId = 0;
    if (clear) {
      canvas.classList.remove('is-on');
      window.setTimeout(function () { if (!running) ctx.clearRect(0, 0, W, H); }, 650);
    }
  }

  /* ---------- Toggle button ---------- */
  var toggle = document.querySelector('[data-rc-snow-toggle]');
  function syncToggle() {
    if (!toggle) return;
    toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = on ? toggle.getAttribute('data-label-off') : toggle.getAttribute('data-label-on');
    toggle.setAttribute('aria-label', label);
    toggle.setAttribute('title', label);
    var text = toggle.querySelector('.rc-snow-toggle__text');
    if (text) text.textContent = label;
  }
  if (toggle) {
    toggle.hidden = false;
    toggle.addEventListener('click', function () {
      on = !on;
      saved = on ? 'on' : 'off';
      try { window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off'); } catch (e) { /* storage unavailable */ }
      syncToggle();
      if (on) start(); else stop(true);
    });
    syncToggle();
  }

  /* ---------- Events ---------- */
  var resizeTimer = 0;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 120);
  }, { passive: true });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop(false); else start();
  });

  if (finePointer) {
    window.addEventListener('pointermove', function (e) {
      pointer.x = e.clientX; pointer.y = e.clientY; pointer.active = true;
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', function () { pointer.active = false; });
  }

  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', function (e) {
      if (saved) return; // the visitor's own choice wins
      on = !e.matches;
      syncToggle();
      if (on) start(); else stop(true);
    });
  }

  findFooter();
  resize();
  if (on) start();
})();
